import { inject, Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ICtaForm } from '@campaign-form/campaign-form.interface';
import { EDeliverableType, EPlatformFeature } from '@enums/campaign.enum';
import { TranslateService } from '@ngx-translate/core';
import { camelCase } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import {
  IDeliverableConfigurationApi,
  TDeliverable,
  TDeliverableForm,
} from './deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-list.interface';
import { DeliverableListService } from './deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-list.service';
import {
  IExecution,
  IExecutionForm,
} from './deliverable-flow-dialog/execution-step/execution-list/execution/execution.interface';
import { ExecutionService } from './deliverable-flow-dialog/execution-step/execution-list/execution/execution.service';

@Injectable({
  providedIn: 'root',
})
export class CtaTemplateCopyService {
  private readonly deliverableListService = inject(DeliverableListService);
  private readonly executionService = inject(ExecutionService);
  private readonly translate = inject(TranslateService);

  public copyCtaTemplate(
    sourceForm: FormGroup<ICtaForm>,
    targetPlatformFeature: EPlatformFeature,
    deliverableConfigurations: IDeliverableConfigurationApi[],
    isAllCreators: boolean,
  ): FormGroup<ICtaForm> {
    const availableDeliverableTypes = deliverableConfigurations.reduce((acc, config) => {
      if (camelCase(config.platformFeature) === camelCase(targetPlatformFeature)) {
        acc.add(camelCase(config.deliverable) as EDeliverableType);
      }

      return acc;
    }, new Set<EDeliverableType>());

    const availableDeliverableForms: FormGroup<TDeliverableForm>['value'][] = [];

    sourceForm.controls.deliverables.value.forEach((deliverableForm) => {
      const deliverableType = deliverableForm.type! as EDeliverableType;
      const deliverableConfig = availableDeliverableTypes.has(deliverableType);

      if (deliverableConfig) {
        availableDeliverableForms.push(deliverableForm);
      }
    });

    return this.copyCtaForm(sourceForm, targetPlatformFeature, availableDeliverableForms, isAllCreators);
  }

  public getCopiedCtaFormValues(
    sourceForm: FormGroup<ICtaForm>,
    targetPlatformFeature?: EPlatformFeature,
    availableDeliverableForms?: FormGroup<TDeliverableForm>['value'][],
    isAllCreators?: boolean,
  ): FormGroup<ICtaForm>['value'] {
    const customCtaValues = this.cloneValue(sourceForm.get('customCtaValues')?.value ?? []);
    const deliverables = this.cloneValue(availableDeliverableForms ?? sourceForm.controls.deliverables.value ?? []);
    const executions: IExecution[] = (this.cloneValue(sourceForm.controls.executions.value ?? []) as IExecution[]).map(
      (execution) => ({
        ...execution,
        name: targetPlatformFeature
          ? this.translate.instant(`shared.platformFeatures.${targetPlatformFeature}`)
          : execution.name,
      }),
    );

    return {
      customCtaValues,
      deliverables,
      exclusiveTo: sourceForm.get('exclusiveTo')?.value,
      executions,
      icon: sourceForm.controls.icon.value,
      id: uuidv4(),
      isAllCreators: isAllCreators !== undefined ? isAllCreators : sourceForm.controls.isAllCreators.value,
      name: targetPlatformFeature
        ? this.translate.instant(`shared.platformFeatureGroups.${targetPlatformFeature}`)
        : sourceForm.controls.name.value,
      platformFeature: targetPlatformFeature ?? sourceForm.controls.platformFeature.value,
    };
  }

  public createCtaFormFromValues(values: FormGroup<ICtaForm>['value']): FormGroup<ICtaForm> {
    return new FormGroup<ICtaForm>({
      customCtaValues: new FormControl(values.customCtaValues ?? [], { nonNullable: true }),
      deliverables: new FormArray<FormGroup<TDeliverableForm>>(
        this.createDeliverableFormGroups(values.deliverables as TDeliverable[]),
      ),
      exclusiveTo: new FormControl(values.exclusiveTo),
      executions: new FormArray<FormGroup<IExecutionForm>>(
        this.createExecutionFormGroups(values.executions as IExecution[], values.platformFeature!),
      ),
      icon: new FormControl(values.icon!),
      id: new FormControl(values.id!),
      isAllCreators: new FormControl(values.isAllCreators!),
      isCtaDetailsValid: new FormControl(true),
      isCustomMode: new FormControl(false),
      name: new FormControl(values.name!, Validators.maxLength(255)),
      platformFeature: new FormControl(values.platformFeature!, {
        nonNullable: true,
      }),
    });
  }

  public createDeliverableFormGroups(deliverables: TDeliverable[]): FormGroup<TDeliverableForm>[] {
    return deliverables.map((deliverable) =>
      this.deliverableListService.createDeliverable(deliverable.type, deliverable),
    ) as FormGroup<TDeliverableForm>[];
  }

  public createExecutionFormGroups(
    executions: IExecution[],
    platformFeature: EPlatformFeature,
  ): FormGroup<IExecutionForm>[] {
    return executions.map((execution) => this.executionService.getConfig().createForm(platformFeature, execution));
  }

  private copyCtaForm(
    sourceForm: FormGroup<ICtaForm>,
    targetPlatformFeature: EPlatformFeature,
    availableDeliverableForms: FormGroup<TDeliverableForm>['value'][],
    isAllCreators: boolean,
  ): FormGroup<ICtaForm> {
    const copiedCtaFormValues = this.getCopiedCtaFormValues(
      sourceForm,
      targetPlatformFeature,
      availableDeliverableForms,
      isAllCreators,
    );

    return this.createCtaFormFromValues(copiedCtaFormValues);
  }

  private cloneValue<T>(value: T): T {
    return structuredClone(value);
  }
}
